import { Prisma } from "@prisma/client";

export const clientUserSelector = {
	id: true,
	firstname: true,
	lastname: true,
	mobilePhone: true,
	email: true,
	address: {
		select: {
			streetNumber: true,
			streetName: true,
			postalCode: true,
			city: true,
		},
	},
} as const satisfies Prisma.UserSelect;

export const clientSocietySelector = {
	id: true,
	denomination: true,
	siren: true,
	companyName: true,
	brokerageFees: true,
	headOfficeAddress: {
		select: {
			streetNumber: true,
			streetName: true,
			postalCode: true,
			city: true,
		},
	},
	syndicUserAccessToPlatform: {
		select: {
			id: true,
			firstname: true,
			lastname: true,
			email: true,
			mobilePhone: true,
			address: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
	managingBrokerSociety: {
		select: {
			id: true,
			denomination: true,
		},
	},
	clientConstructionSites: {
		select: {
			id: true,
		},
	},
	syndicConstructionSites: {
		select: {
			id: true,
		},
	},
	users: {
		where: { role: "INSURED" },
		orderBy: { createdAt: "asc" },
		take: 1,
		select: {
			id: true,
			firstname: true,
			lastname: true,
			email: true,
			mobilePhone: true,
			address: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
} as const satisfies Prisma.SocietySelect;

export const clientDetailsSelector = {
	id: true,
	clientType: true,
	clientIsMOA: true,
	moaClientType: true,
	clientSociety: {
		select: clientSocietySelector,
	},
	moaClientSociety: {
		select: clientSocietySelector,
	},
	moaClientContact: {
		select: clientUserSelector,
	},
	constructionSiteClientContacts: {
		include: {
			constructionSiteContact: {
				select: {
					id: true,
					firstname: true,
					lastname: true,
					email: true,
					mobilePhone: true,
					address: {
						select: {
							streetNumber: true,
							streetName: true,
							postalCode: true,
							city: true,
						},
					},
				},
			},
		},
	},
	ConstructionSite: {
		select: {
			id: true,
			createdAt: true,
			status: true,
			brokerSociety: {
				select: {
					denomination: true,
				},
			},
		},
	},
} as const satisfies Prisma.ConstructionSiteClientDetailsSelect;

export type ClientDetailsWithRelations = Prisma.ConstructionSiteClientDetailsGetPayload<{
	select: typeof clientDetailsSelector;
}>;


export type ClientListItem = {
	id: number;
	clientType: "USER" | "SOCIETY";
	roles: string;
	firstname: string | null;
	lastname: string | null;
	email: string | null;
	mobilePhone: string | null;
	denomination: string | null;
	siren: string | null;
	companyName: string | null;
	brokerName: string | null;
	allConstructionSites: ClientConstructionSiteInfo[];
	totalConstructionSitesCount: number;
};

// Types pour la page détail client
export type ClientDetailAddress = {
	streetNumber: string | null;
	streetName: string;
	postalCode: string;
	city: string;
};

export type ClientDetailContact = {
	id: number;
	firstname: string;
	lastname: string;
	email: string | null;
	mobilePhone: string | null;
	address: ClientDetailAddress | null;
};

export type ClientDetailSociety = {
	id: number;
	denomination: string;
	siren: string | null;
	companyName: string | null;
	brokerageFees: number | null;
	headOfficeAddress: ClientDetailAddress | null;
	syndicUserAccessToPlatform: ClientDetailContact | null;
	managingBrokerSociety: { id: number; denomination: string } | null;
	clientConstructionSites: { id: number }[];
	syndicConstructionSites: { id: number }[];
	users: ClientDetailContact[];
};

export type ClientDetailItem = {
	id: number;
	clientType: "USER" | "SOCIETY";
	user: ClientDetailContact | null;
	society: ClientDetailSociety | null;
};

export type ClientConstructionSiteInfo = {
	id: number;
	status: string;
	createdAt: Date;
	ref: string;
};

export type ClientDetailsWithConstructionSites = ClientDetailsWithRelations & {
	allConstructionSites: ClientConstructionSiteInfo[];
	totalConstructionSitesCount: number;
	roles: string;
};

export type ClientUserSelect = Prisma.UserGetPayload<{
	select: typeof clientUserSelector;
}>;

export type ClientSocietySelect = Prisma.SocietyGetPayload<{
	select: typeof clientSocietySelector;
}>;

const clientPropositionConstructionSiteSelect = {
	id: true,
	constructionSiteOffers: {
		select: {
			binderWarranty: {
				select: {
					warrantyType: true,
				},
			},
			pricingDetails: {
				select: {
					type: true,
					totalClientPrimeTTC: true,
				},
			},
		}
	},
	constructionSiteDetails: {
		select: {
			address: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
}
// Sélecteurs pour les propositions, contrats et reçus des clients
export const clientPropositionSelect = {
	id: true,
	reference: true,
	status: true,
	productType: true,
	assureurName: true,
	primeTTC: true,
	createdAt: true,
	constructionSite: {
		select: clientPropositionConstructionSiteSelect
	},
	gfaConstructionSite: {
		select: clientPropositionConstructionSiteSelect
	},
} satisfies Prisma.PropositionSelect;


const clientContractConstructionSiteSelect = {
	id: true,
	constructionSiteDetails: {
		select: {
			address: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
}

export const clientContractSelect = {
	id: true,
	reference: true,
	status: true,
	productType: true,
	assureurName: true,
	primeTTC: true,
	contractStartDate: true,
	contractEndDate: true,
	signedAt: true,
	createdAt: true,
	constructionSite: {
		select: clientContractConstructionSiteSelect
	},
	gfaConstructionSite: {
		select: clientContractConstructionSiteSelect
	},
} satisfies Prisma.PropositionSelect;


const clientReceiptConstructionSiteSelect = {
	id: true,
	constructionSiteDetails: {
		select: {
			address: {
				select: {
					streetNumber: true,
					streetName: true,
					postalCode: true,
					city: true,
				},
			},
		},
	},
}

export const clientReceiptSelect = {
	id: true,
	reference: true,
	type: true,
	productType: true,
	issueDate: true,
	status: true,
	pricingDetails: {
		select: {
			duneManagementFeesTTC: true,
			intermediaryManagementFeesTTC: true,
			brokerCommission: true,
			brokerRemuneration: true,
		},
	},
	proposition: {
		select: {
			id: true,
			reference: true,
			constructionSite: {
				select: clientReceiptConstructionSiteSelect
			},
			gfaConstructionSite: {
				select: clientReceiptConstructionSiteSelect
			},
		},
	},
} satisfies Prisma.ReceiptSelect;
